import { type SchedulingSlot } from '../../../../models/Scheduling'
import { toLocalISODate } from '../../../../common/dateUtils'
import { getDateTimeFormatter } from '../../../../common/intlFormatters'
import { parseISODateAsLocal } from '../../bookingFormat'

// A proposed time slot in the schedule-meeting flow form (not persisted; the booking carries start/end).
export interface BookingSlot {
  startAt: string
  endAt: string
}

const DAY_KEY_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' }

const BOUNDS_PADDING_DAYS = 2

export const dayKey = (day: Date): string => toLocalISODate(day)

export const zonedDayKey = (instant: Date, timeZone?: string): string => {
  if (!timeZone) {
    return toLocalISODate(instant)
  }
  const parts = getDateTimeFormatter(DAY_KEY_OPTIONS, timeZone).formatToParts(instant)
  const valueOf = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${valueOf('year')}-${valueOf('month')}-${valueOf('day')}`
}

export const monthBounds = (monthDate: Date): { dateFrom: string; dateTo: string } => {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1 - BOUNDS_PADDING_DAYS)
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1 + BOUNDS_PADDING_DAYS)
  return { dateFrom: start.toISOString(), dateTo: end.toISOString() }
}

export const groupSlotsByDay = (slots: SchedulingSlot[], timeZone?: string): Map<string, BookingSlot[]> => {
  const sorted = [...slots].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
  const byDay = new Map<string, BookingSlot[]>()
  const seen = new Set<string>()
  for (const slot of sorted) {
    const slotKey = `${slot.start}/${slot.end}`
    if (seen.has(slotKey)) {
      continue
    }
    seen.add(slotKey)
    const key = zonedDayKey(new Date(slot.start), timeZone)
    const daySlots = byDay.get(key) ?? []
    daySlots.push({ startAt: slot.start, endAt: slot.end })
    byDay.set(key, daySlots)
  }
  return byDay
}

export const firstAvailableDay = (slots: SchedulingSlot[], timeZone?: string): Date | null => {
  if (slots.length === 0) {
    return null
  }
  const earliest = slots.reduce((min, slot) => (new Date(slot.start).getTime() < new Date(min.start).getTime() ? slot : min))
  return parseISODateAsLocal(zonedDayKey(new Date(earliest.start), timeZone))
}
